import { useEffect, useState } from "react";
import { Box, Button, Typography, Stack, Grid } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import MpgInput from "../../../../CommonFormComp/MpgInput";
import MpgItem from "../../../../CommonFormComp/MpgItem";
import MpgAutocompleteSingle from "../../../../CommonFormComp/MpgAutocompleteSingle";
import {
  useGetSubAcccountByClientAccountIdQuery,
  useGetLanguageListByClientAccountOrClientSubAccountId,
  useCreateClientUserMutation,
  useGetClientAccountDropdownMutation} from "../../../../reactQueries/AppApi/rqHooksForClientUsers";
import MpgServiceOfficeDialog from "../../../../CommonFormComp/MpgServiceOfficeListDialog/MpgServiceOfficeDialog";
import { useGetPermissionsListQuery, useGetDashboardAccessListQuery, useGetServiceOfficeListMutation, useGetSiteCoreCMSData,useGetClientTypeClassificationQuery } from "../../../../reactQueries/commonRQHooks";
import CircularProgress from '@mui/material/CircularProgress';
import { clientUserViewPath } from "../../../../routes/routePaths";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../common/dialog/MpgLoadingDialog";
import MpgAutocompleteMulti from "../../../../CommonFormComp/MpgAutocompleteMulti";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getCLPermissions } from "../../../../hooksAndFunctions/functions/getCLPermissions";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";
import { useGetAccountAndSubAccListOnlyForA4Role } from "../../../../hooksAndFunctions/customHooks/useGetAccountAndSubAccListOnlyForA4Role";
import { BaseSchema } from "../BaseSchema";

const CreateClientUser = () => {
  let navigate = useNavigate();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;

  const userName = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
    "name"
  ];

  const {
    clientPortalAdminType
  } = getAccessTypes()
  const {
    hasA2Access,
    hasA4Access
  } = clientPortalAdminType

  /**useful for only A4 role */
  const { clientAccountIdListForA4Role, clientSubAccountIdListForA4Role } = useGetAccountAndSubAccListOnlyForA4Role({ userId })

  // schema validations
  const schema = yup.object().shape({
    ...BaseSchema,
    client_sub_account: yup.object().label('This field')
      .when('permission_level', (permission_level, yup) => {
        const { CL1_CL2_CL9_Permission } = getCLPermissions({ selectedPermissionLabel: permission_level?.selectOptionLabel })

        if (!CL1_CL2_CL9_Permission) {
          return yup.required().typeError('This field is a required')
        } else {
          return yup.nullable()
        }
      }),
    // .optional().nullable(),
    service_office: yup.string()
      .when('permission_level', (permission_level, yup) => {
        const { CL6_CL7_CL8_Permission } = getCLPermissions({ selectedPermissionLabel: permission_level?.selectOptionLabel })

        if (!!CL6_CL7_CL8_Permission) {
          return yup.required("This field is required").typeError('This field is required')
        } else {
          return yup.optional()
        }
      }),
    dashboard_access: yup.array()
      .when('permission_level', (permission_level, yup) => {
        const { CL4_CL7_Permission } = getCLPermissions({ selectedPermissionLabel: permission_level?.selectOptionLabel })

        if (!!CL4_CL7_Permission) {
          return yup.optional()
        } else {
          /**way to handle required for yup array */
          return yup.min(1, "This field is required").typeError('This field is required')
        }
      }),
    pref_lang: yup.object()
      .required("This field is required")
      .typeError("This field is required"),
  });

  const {data: adminClientUserCMSDataFromAPI} = useGetSiteCoreCMSData({pagename: "ClientUsers"});
  const { data: ClientTypeList } = useGetClientTypeClassificationQuery();


  const [clientUsersCMSData, setClientUsersCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
  });
  const { watch, setValue } = rhfFormInstance;

  const selectedPermissionLevel = watch("permission_level");
  const selectedClientAccount = watch("client_account");
  const selectedClientSubAccount = watch("client_sub_account");

  const selectedPermissionLabel = selectedPermissionLevel?.selectOptionLabel || ""
  /** getting permissions type */
  const [CLPermission, setCLPermission] = useState({})

  const { CL1_CL2_CL9_Permission, CL3_CL4_CL5_Permission, CL6_CL7_CL8_Permission, CL4_CL7_Permission } = CLPermission

  useEffect(() => {
    if (!!selectedPermissionLabel) {
      const CLPermission = getCLPermissions({ selectedPermissionLabel })
      setCLPermission(CLPermission)
    } else {
      setCLPermission({})
    }
  }, [selectedPermissionLabel])

  // api - get permissions list
  const {
    data: permissionListData,
  } = useGetPermissionsListQuery();

  // api - get client accounts list
  const { mutate: getClientAccountList,
    data: clientAccountList } = useGetClientAccountDropdownMutation();

  useEffect(() => {
    getClientAccountList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const clientAccountListData = clientAccountList?.data

  // api - get dashboard access list
  const { data: dashboardAccessListData,
  } = useGetDashboardAccessListQuery();

  const { data: clientSubAccListData,
  } = useGetSubAcccountByClientAccountIdQuery({ ClientAccountId: selectedClientAccount?.id });

  const {
    mutate: getServiceOfficeListData,
    isLoading: isLoadingOnServiceOfficeList,
    isSuccess: isSuccessOnServiceOfficeList,
    data: serviceOfficeListData,
  } = useGetServiceOfficeListMutation();

  useEffect(() => {
    getServiceOfficeListData({ body: [] })
  }, [])

  // /**disable dependent fields */
  const [disableClientAccount, setDisableClientAccount] = useState(true)
  const [disableClientSubAccount, setDisableClientSubAccount] = useState(true)
  const [disableServiceOffice, setDisableServiceOffice] = useState(true)
  const [disableLangList, setDisableLangList] = useState(true)

  const [clientAccountOrClientSubAccountId, setClientAccountOrClientSubAccountId] = useState('')

  useEffect(() => {

    if (!!selectedPermissionLevel?.selectOptionLabel) {
      /**lang */
      setDisableLangList(false)

      if (!!CL1_CL2_CL9_Permission) {
        setClientAccountOrClientSubAccountId(selectedClientAccount?.id)

        setDisableClientAccount(false)
        setDisableClientSubAccount(true)
        setDisableServiceOffice(true)

        setValue('client_sub_account', "")
        setValue('service_office', "")

      } else if (!!CL3_CL4_CL5_Permission) {
        setClientAccountOrClientSubAccountId(selectedClientSubAccount?.id)

        setDisableClientAccount(false)
        setDisableClientSubAccount(false)
        setDisableServiceOffice(true)

        setValue('service_office', "")
      } else {
        setClientAccountOrClientSubAccountId(selectedClientSubAccount?.id)

        setDisableClientAccount(false)
        setDisableClientSubAccount(false)
        setDisableServiceOffice(false)
      }
    }
  }, [selectedPermissionLevel, selectedClientSubAccount, selectedClientAccount, CLPermission])

  const { data: langListByClientAccountOrClientSubAccountIdData,
    isSuccess: isSuccessOnlangListByClientAccountOrClientSubAccountIdData,
  } = useGetLanguageListByClientAccountOrClientSubAccountId({ ClientAccountOrClientSubAccountId: clientAccountOrClientSubAccountId });


  // api - create client user mutation
  const {
    mutate: createClientUser,
    isError: isErrorOnCreate,
    isLoading: isLoadingOnCreate,
    isSuccess: isSuccessOnCreate,
    error: errorOnCreate,
  } = useCreateClientUserMutation();

  const permissionAutocompleteList = !!permissionListData && permissionListData.map((item) => {
    return {
      id: item.PermissionId,
      selectOptionLabel: `${item.PermissionCode} - ${item.PermissionDescription}`,
      selectOptionValue: item.PermissionId,
      extraData: item
    }
  })

  const ClientTypeAutocompleteList = !!ClientTypeList && ClientTypeList.map((item) => {
        return {
            id: item.DataClassificationKey,
            selectOptionLabel: `${item.MasterDataClassificationName}`,
            selectOptionValue: item.DataClassificationKey,
        }
    })

  const clientAccountAutocompleteList = !!clientAccountListData && clientAccountListData
    /**for create only passing only active accounts */
    .filter((item) => !!item.IsActive)
    .map((item) => {
      const disableOptionForA4Role = !clientAccountIdListForA4Role.includes(item?.Id)

      let disableOption = false

      if ((!!hasA4Access || !!hasA2Access) && !!disableOptionForA4Role) {
        if (!!CL1_CL2_CL9_Permission) {
          disableOption = true
        } else {
          disableOption = false
        }
      } else {
        /**for rest of roles */
        disableOption = false
      }
      return {
        id: item.Id,
        selectOptionLabel: `${item.ClientAccountName}`,
        selectOptionValue: item.Id,
        selectOptionDisabled: !!disableOption,
        extraData: item
      }
    })
  /**need change from API team */
  const clientSubAcctAutocompleteList = !!clientSubAccListData && clientSubAccListData
  //const clientSubAcctAutocompleteList = !!clientSubAccListData?.data && clientSubAccListData?.data
    /**for create only passing only active sub-accounts */
    .filter((item) => !!item.IsActive)
    .map((item) => {
      const disableOptionForA4Role = !clientSubAccountIdListForA4Role.includes(item?.Id)

      let disableOption = false
      /**(only for A4 role) get all clinet sub acc ids where this user is not A4 admin and diable those autocomplete options */
      if ((!!hasA4Access || !!hasA2Access) && !!disableOptionForA4Role) {
        disableOption = true
      } else {
        /**for rest of roles */
        disableOption = false
      }
      return {
        id: item.Id,
        selectOptionLabel: `${item.ClientAccountName}`,
        selectOptionValue: item.Id,
        selectOptionDisabled: !!disableOption,
        extraData: item
      }
    })

  const dashBoardAccessAutocompleteList = !!dashboardAccessListData && dashboardAccessListData.map((item) => {
    return {
      id: item.id,
      selectOptionLabel: `${item.TypeDescription}`,
      selectOptionValue: item.id,
      extraData: item
    }
  })
  /**dashboard field control on CL4 and CL7 */
  useEffect(() => {
    if (!!selectedPermissionLevel?.selectOptionLabel) {
      if (!!CL4_CL7_Permission) {
        /** clearing dashboard values on CL4 and CL7 */
        setValue("dashboard_access", [])
      } else {
        /**by default all values needs to be selected */
        setValue("dashboard_access", dashBoardAccessAutocompleteList, { shouldValidate: true })
      }
    }
  }, [selectedPermissionLevel])

  /**preferred language list */
  const [prefLangList, setPrefLangList] = useState()
  useEffect(() => {
    if (!!isSuccessOnlangListByClientAccountOrClientSubAccountIdData && !!langListByClientAccountOrClientSubAccountIdData) {

      const list = !!langListByClientAccountOrClientSubAccountIdData && langListByClientAccountOrClientSubAccountIdData.map((item) => {
        return {
          id: item.LanguageID,
          selectOptionLabel: `${item.LanguageCode} - ${item.LanguageDescription}`,
          selectOptionValue: item.LanguageID,
          extraData: item
        }
      })
      setPrefLangList(list)
    }
  }, [isSuccessOnlangListByClientAccountOrClientSubAccountIdData])
  /**service office handling */
  /**service office dialog state */
  const [openServiceOfficeDialog, setOpenServiceOfficeDialog] = useState(false);

  /**service office selected data state */
  const [serviceOfficeItemsFromDialog, setServiceOfficeItemsFromDialog] = useState([])

  useEffect(() => {
    if (!!serviceOfficeItemsFromDialog && serviceOfficeItemsFromDialog.length) {
      const list = serviceOfficeItemsFromDialog.map((item) => {
        return item.masterRightOfficeName
      })
      setValue('service_office', list.toString())
    } else {
      setValue("service_office", "");
    }
  }, [serviceOfficeItemsFromDialog])

  const onSubmt = (data) => {

    let serviceOfficeListing = []
    if (!!serviceOfficeItemsFromDialog) {
      serviceOfficeItemsFromDialog.forEach((item) => {
        serviceOfficeListing.push({
          rightOfficeKey: item?.rightOfficeKey,
        })
      })
    }

    let clientSubAccIdTobeSent = ""
    let serviceOfficeListTobeSent = []
    if (!!CL1_CL2_CL9_Permission) {
      clientSubAccIdTobeSent = "";
      serviceOfficeListTobeSent = [];
    }
    else if (!!CL3_CL4_CL5_Permission) {
      clientSubAccIdTobeSent = data?.client_sub_account?.id;
      serviceOfficeListTobeSent = [];
    }
    else if (!!CL6_CL7_CL8_Permission) {
      clientSubAccIdTobeSent = data?.client_sub_account?.id;
      serviceOfficeListTobeSent = serviceOfficeListing;
    }

    let dashboardAccessIdis = [];
    if (!!data?.dashboard_access && !!data?.dashboard_access?.length) {
      const listing = []
      data?.dashboard_access.forEach((item) => {
        listing.push(item?.id)
      })
      dashboardAccessIdis = listing
    }
    const body = {
      UserId: userId,
      FirstName: data?.first_name || '',
      LastName: data?.last_name || '',
      EmailAddress: data?.email || '',
      JobRole: data?.job_role || '',

      PermissionId: data?.permission_level.id,
      ClientAccountId: data?.client_account?.id,
      DataClassificationKey:data?.ClientType?.id,
      ClientSubAccountId: clientSubAccIdTobeSent,
      ClientAccountUserServiceOffice: serviceOfficeListTobeSent,
      ClientUserDashboardId: dashboardAccessIdis || [],
      LanguageId: data?.pref_lang?.id,
      CreatedBy: userName,
    };
    //console.log('TESTBODY',body)
    createClientUser({ body });
  };

  useEffect(() => {
    if (!!isSuccessOnCreate) {
      navigate(clientUserViewPath);
    }
  }, [isSuccessOnCreate]);

  /**clearing service office selections in dialog if permissions does not belongs to cl6 cl7 cl8 */
  useEffect(() => {
    if (!CL6_CL7_CL8_Permission) {
      setServiceOfficeItemsFromDialog([])
    }
  }, [selectedPermissionLabel, CLPermission])

  /**handling error states and msgs */
  const [errorTitle, setErrorTitle] = useState("")
  const [errorContent, setErrorContent] = useState("")
  useEffect(() => {
    if (!!isErrorOnCreate && errorOnCreate?.response?.data === "EmailExists") {
      setErrorTitle("Email exists. Try again...!")
      setErrorContent(`"${watch('email')}" exists already in our records. Please, try again with different email id.`)
    } else if (!!isErrorOnCreate && errorOnCreate?.response?.data === "NoAccessForA4Role") {
      setErrorTitle("Unauthorized action...!")
      setErrorContent("You are not authorized to perform this action.")
    } else {
      setErrorTitle('')
      setErrorContent("")
    }
  }, [isErrorOnCreate, errorOnCreate])

  const fetchClientUsersCMSData = () =>{
    if(adminClientUserCMSDataFromAPI && adminClientUserCMSDataFromAPI.Pages){
      adminClientUserCMSDataFromAPI.Pages.map((el) => {
       if (el.Key === "ClientUsers") {
         const { Title, Description, TabNavigationTitle, Components } = el;
         setClientUsersCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
       }
   });
    }
  }

  const fetchClientUserListLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["ClientUsersList"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["ClientUsersList"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchClientUserDtlsLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["ClientUsersDetails"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["ClientUsersDetails"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchClientUserAccDtlsLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["ClientUsersAccessDetails"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["ClientUsersAccessDetails"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  // const fetchCommonCMSLabel = (key) => {
  //   if (clientUsersCMSData && clientUsersCMSData?.pageContent["CommonComponent"]) {
  //       const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["CommonComponent"];
  //       return returnLabel(meetingReqCMSDataLabel?.Labels, key)
  //   }
  //   return '';
  // }

  const returnLabel = (labelArray=[], key) => {
        return labelArray.find((x) => x.Key === key)?.Phrase || '';
  }

  useEffect(() => {
    fetchClientUsersCMSData();
   }, [adminClientUserCMSDataFromAPI]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {/* Create New Client User */}
          {clientUsersCMSData.pageContent["ClientUsersDetails"]?.Title}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={clientUserViewPath}
          >
            {fetchClientUserDtlsLabel('ClientUsersDetails.ClientUsers') }
          </Button>

          <Button
            variant="outlined"
            size="small"
            disabled
          >
            {fetchClientUserDtlsLabel('ClientUsersDetails.DeactivateUser') } 
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmt)}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <MpgItem sx={{ padding: { xs: "20px" } }}>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  {fetchClientUserDtlsLabel('ClientUsersDetails.ClientUserDetails') } 
                </Typography>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"first_name"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.FirstName')} //{ "First Name"}
                  fullWidth
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"last_name"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.LastName')} //{"Last Name"}
                  fullWidth
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"email"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.Email')} //{"Email"}
                  fullWidth
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"job_role"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.JobRole')} //{"Job Role"}
                  fullWidth
                />

                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"status"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.Status')} //{"Status"}
                  fullWidth
                  disabled={true}
                />                
                 <MpgAutocompleteSingle 
                  rhfFormInstance={rhfFormInstance}
                  name={"ClientType"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.Type')} 
                  fullWidth
                  optionList={ClientTypeAutocompleteList || []}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"isRegistered"} 
                  label={fetchClientUserListLabel('ClientUsersList.Registration')} //{"Registration"}
                  disabled
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled
                >
                  {fetchClientUserDtlsLabel('ClientUsersDetails.RemoveUser')}
                </Button>

                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled
                >
                  {fetchClientUserDtlsLabel('ClientUsersDetails.ResendInvitationEmail')}
                </Button>
              </MpgItem>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MpgItem>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  {clientUsersCMSData.pageContent["ClientUsersAccessDetails"]?.Title}
                </Typography>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"permission_level"}
                  label={fetchClientUserListLabel('ClientUsersList.PermissionLevel')} //{"Permission Level"}
                  fullWidth
                  optionList={permissionAutocompleteList || []}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"client_account"}
                  label={fetchClientUserListLabel('ClientUsersList.ClientAccount')} //{"Client Account"}
                  fullWidth
                  optionList={clientAccountAutocompleteList || []}
                  onChangeExtraHandler={() => {
                    setValue("client_sub_account", "");
                    setValue("service_office", "");
                    setValue("pref_lang", "");
                  }}
                  disabled={disableClientAccount}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"client_sub_account"}
                  label={fetchClientUserListLabel('ClientUsersList.ClientSubAccount')} //{"Client Sub-Account"}
                  fullWidth
                  optionList={clientSubAcctAutocompleteList || []}
                  onChangeExtraHandler={() => {
                    setValue("service_office", "");
                    setValue("pref_lang", "");
                  }}
                  disabled={disableClientSubAccount}
                />

                <div>
                  <MpgInput
                    rhfFormInstance={rhfFormInstance}
                    label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.ServiceOffices')} //{'Service Offices'}
                    name={'service_office'}
                    multiline
                    rows={4}
                    fullWidth
                    disabled={true}
                  />
                  {

                    !!isLoadingOnServiceOfficeList && (
                      <Button
                        disabled
                      >
                        <CircularProgress
                          size={15}
                          sx={{ margin: '5px' }}
                        />
                        Fetching...
                      </Button>
                    )
                  }
                  {
                    !!isSuccessOnServiceOfficeList && (
                      <Button
                        onClick={() => { setOpenServiceOfficeDialog(true) }}
                        disabled={!!disableServiceOffice}
                      >
                        {/* Select Service Offices */}
                        {fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.SelectServiceOffices')}
                      </Button>
                    )}
                </div>

                <MpgAutocompleteMulti
                  rhfFormInstance={rhfFormInstance}
                  name={"dashboard_access"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.DashboardAccess')} //{"Dashboard Access"}
                  fullWidth
                  optionList={dashBoardAccessAutocompleteList || []}
                  disabled={!!CL4_CL7_Permission}
                />

                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"pref_lang"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.PreferredLanguage')} //{"Preferred Language"}
                  fullWidth
                  optionList={prefLangList || []}
                  disabled={!!disableLangList}
                />
              </MpgItem>
            </Grid>
          </Grid>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-evenly"}
            mt={3}
          >
            <Button variant="outlined" type="submit" size="small">
              {/* Create User */}
              {fetchClientUserDtlsLabel('ClientUsersDetails.CreateUser')}
            </Button>
          </Stack>
        </Box>

        {  /**dialog */}
        <MpgServiceOfficeDialog
          open={openServiceOfficeDialog}
          setOpen={setOpenServiceOfficeDialog}

          serviceOfficeItemsFromDialog={serviceOfficeItemsFromDialog}
          setServiceOfficeItemsFromDialog={setServiceOfficeItemsFromDialog}

          serviceOfficeListFromAPI={serviceOfficeListData?.data || []}
        />

        {/** error dialog on create action */}

        <MpgErrorDialog
          open={!!isErrorOnCreate}
          title={errorTitle || ''}
          content={errorContent || ''}
        />

        <MpgLoadingDialog
          open={!!isLoadingOnCreate}
        />

      </Box>
    </>
  );
};

export default CreateClientUser;
